import { NOT_FOUND } from '@jira/platform__http-status-codes/src/http-status-codes.tsx';
import type { LocalViewId } from '@jira/polaris__domain-view/src/ViewKind.types.tsx';
import { experience } from '@jira/polaris__lib-analytics/src/ConstantsExperience.tsx';
import { isPermissionError } from '@jira/polaris__lib-errors/src/isPermissionError.tsx';
import { sendPendoTrackEvent } from '@jira/polaris__lib-analytics/src/sendPendoTrackEvent.tsx';
import { isClientFetchError } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';
import type { Props, State } from './NavigationView.types.tsx';
import { withRefreshBlocker } from './withRefreshBlocker.tsx';
import { findView, modifyView, updateContainerState } from './updateContainerState.tsx';

// oxlint-disable-next-line typescript/consistent-type-assertions
const toLowerCase = <T extends string>(str: T) => str.toLowerCase() as Lowercase<T>;

export const deleteView = (localId: LocalViewId): Action<State, Props> =>
	withRefreshBlocker(
		async (
			{ setState, getState },
			{
				navigationRemote,
				cloudId,
				containerType,
				containerId,
				onFailure,
				onSuccess,
				createAnalyticsEvent,
			},
		) => {
			if (
				!cloudId ||
				!containerId ||
				!navigationRemote ||
				getState().containers[containerId]?.isLoading ||
				!getState().containers[containerId]?.initialized
			) {
				return;
			}

			const { view } = findView(getState().containers[containerId], localId);

			if (!view) {
				return;
			}

			setState(
				modifyView(getState(), containerId, (viewToModify) => {
					if (viewToModify.id === localId) {
						return {
							...viewToModify,
							isLocked: true,
						};
					}
					return viewToModify;
				}),
			);

			const analyticsMetadata = Object.freeze({
				location: 'navigation',
				containerType: toLowerCase(containerType),
				viewId: String(view.polarisId),
			});

			try {
				experience.view.deleteView.start();

				await navigationRemote.deleteView({ viewId: view.id });
				setState(
					updateContainerState(getState(), containerId, {
						views: getState()
							.containers[containerId].views.filter((innerView) => innerView.localId !== localId)
							.map((childView) => {
								if (childView.views) {
									return {
										...childView,
										views: childView.views?.filter((innerView) => innerView.localId !== localId),
									};
								}
								return childView;
							}),
					}),
				);
				onSuccess?.('deleteView');

				if (createAnalyticsEvent) {
					fireTrackAnalytics(createAnalyticsEvent({}), 'view deleted', analyticsMetadata);
				}

				sendPendoTrackEvent({
					actionSubjectAndAction: 'view deleted',
					attributes: analyticsMetadata,
				});

				experience.view.deleteView.success({
					metadata: analyticsMetadata,
				});
				// oxlint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				if (error.statusCode === NOT_FOUND) {
					setState(
						updateContainerState(getState(), containerId, {
							views: getState()
								.containers[containerId].views.filter((innerView) => innerView.localId !== localId)
								.map((childView) => {
									if (childView.views) {
										return {
											...childView,
											views: childView.views?.filter((innerView) => innerView.localId !== localId),
										};
									}
									return childView;
								}),
						}),
					);
					experience.view.deleteView.abort(error);

					return;
				}

				setState(
					modifyView(getState(), containerId, (innerView) => {
						if (innerView.localId === localId) {
							return {
								...innerView,
								isLocked: false,
							};
						}
						return innerView;
					}),
				);

				if (isPermissionError(error) || isClientFetchError(error)) {
					experience.view.deleteView.abort(error, {
						metadata: analyticsMetadata,
					});
				} else {
					experience.view.deleteView.failure(error, {
						metadata: analyticsMetadata,
					});
				}

				onFailure?.(error, 'deleteView');
			}
		},
	);
